import { post, get } from './apiDetour';
import config from 'config';
let { MEETING_API, SIGNED_API, STREAM_API, QUESTIONS_CF_API } = config;

/**
 * Checks if a meeting has been created
 * @param {string} externalMeetingId - the meeting id (not chime instance id)
 * @return {Boolean || null} - whether or not the meeting has been created
 **/
export const hasMeetingBeenCreated = (ExternalMeetingId: string) =>
  new Promise(async (resolve) => {
    const response = await post(`${MEETING_API}/hasmeetingbeencreated`, { ExternalMeetingId });
    resolve(response?.data);
  });

/**
 * Gets user by id or email
 * @param {string || null} id - the user id (not the chime attendee id)
 * @param {string || null} email - the users email
 * @return {object || null} - if user is found returns user object
 **/
export const getUser = (id: string = '', email: string = '', cmsId: string = '') =>
  new Promise(async (resolve) => {
    const response = await post(`${MEETING_API}/getuser`, { id, email, cmsId });
    resolve(response?.data);
  });

export const getUserNames = (ids: string[]) =>
  new Promise(async (resolve) => {
    const response = await post(`${MEETING_API}/getusernames`, { ids });
    resolve(response?.data);
  });

export const openedVod = (VodId: string) =>
  new Promise(async (resolve) => {
    const response = await post(`${STREAM_API}/updatevodcount`, { VodId });
    resolve(response?.data);
  });

export const watchingVod = (VodId: string, Seconds: number, SeekPosition: number, UserId: string) =>
  new Promise(async (resolve) => {
    const response = await post(`${STREAM_API}/updatevodstats`, {
      VodId,
      Seconds,
      SeekPosition,
      UserId,
    });
    resolve(response?.data);
  });

// export const getRecordingUser = (SessionId: String) =>
//   new Promise(async (resolve) => {
//     const response = await post(`${MEETING_API}/getrecordinguser`, { SessionId });
//     resolve(response?.data);
//   });

/**
 * Create User
 * @param {string} firstName - Users first name
 * @param {string} lastName - Users last name
 * @param {string} type - User type of either Student or Teacher
 * @param {boolean} moodle - User is a moodle user
 * @param {boolean} admin - Users admin status
 * @param {string} email - Users email
 * @param {string} password - the users password
 * @return {object || null} - user object
 **/
export const createUser = (
  firstName: string,
  lastName: string,
  type: string,
  moodle: boolean,
  admin: boolean,
  email: string,
  password: string,
  cmsId: string
) =>
  new Promise(async (resolve) => {
    try {
      const response = await post(`${MEETING_API}/createuser`, {
        firstName,
        lastName,
        type,
        moodle: true,
        admin: false,
        email,
        password,
        cmsId,
      });

      resolve(response?.data);
    } catch (e) {
      resolve({ error: e });
    }
  });

/**
 * Checks user email and password
 * @param {string} email - Users email
 * @param {string} password - User password
 * @return {object || null} - if user is authorized returns user object
 **/
export const authUser = (email: string, password: string) =>
  new Promise(async (resolve) => {
    const response = await post(`${MEETING_API}/authuser`, { email: email, password: password });
    resolve(response?.data?.user);
  });

/**
 * Gets token for admin
 * @param {string} role - Users email
 * @param {string} sessionId - User password
 * @return {object || null} - if user is authorized returns user object
 **/
export const getTokenForAdmin = (role: string, sessionId: string) =>
  new Promise(async (resolve) => {
    const response = await post(`${MEETING_API}/gettokenforadmin`, { role, sessionId });
    resolve(response?.data);
  });

/**
 * Gets all meeting instances from external meeting id
 * @param {string} externalMeetingId - External meeting id
 * @return {object || null} - Returns object containing instance data
 **/
export const meetingInstances = (ExternalMeetingId: string) =>
  new Promise(async (resolve) => {
    const response = await post(`${MEETING_API}/meetinginstances`, { ExternalMeetingId });
    resolve(response?.data);
  });

/**
 * Gets information about an external meeting
 * @param {string} externalMeetingId - External meeting id
 * @return {object || null} - Returns object containing instance data
 **/
export const meetingInfo = (ExternalMeetingId: string) =>
  new Promise(async (resolve) => {
    const response = await post(`${MEETING_API}/getmeetinginfo`, { ExternalMeetingId });
    resolve(response?.data);
  });

/**
 * Joins or creates a meeting of the external meeting id
 * @param {string} externalMeetingId - Meeting Id
 * @param {string} externalUserId - Users Id
 * @param {string} region - AWS region. 'us-east-1' by default
 * @return {object || null} - If meeting is created the JoinInfo object will be returned
 **/
export const joinMeeting = (
  externalMeetingId: string,
  externalUserId: string,
  region: string = 'us-east-1'
) =>
  new Promise(async (resolve) => {
    try {
      const response = await post(`${MEETING_API}/join`, {
        externalMeetingId,
        region,
        externalUserId,
      });
      if (response.error) {
        throw response.error;
      }
      resolve(response?.data);
    } catch (err) {
      resolve({ error: err });
    }
  });

/**
 * Ends meeting
 * @param {string} externalMeetingId - Meeting Id
 * @param {string} ChimeMeetingId - Chime meeting instance Id
 * @return {String || null} - if meeting is ended will return success string
 **/
export const endMeeting = (ExternalMeetingId: string, ChimeMeetingId: string) =>
  new Promise(async (resolve) => {
    const response = await post(`${MEETING_API}/end`, { ExternalMeetingId, ChimeMeetingId });
    resolve(response?.data);
  });

/**
 * Gets the Chime attendee info
 * @param {string} externalMeetingId - Meeting Id
 * @return {Object || null} - AttendeeInfo {attendeeId, Name, userId}
 **/
export const attendee = (ExternalUserId: string) =>
  new Promise(async (resolve) => {
    const response = await post(`${MEETING_API}/attendee`, { ExternalUserId });
    resolve(response?.data);
  });

/**

**/
export const assignHost = (ExternalMeetingId: string, hostId: string, ChimeMeetingId: string) =>
  new Promise(async (resolve) => {
    const response = await post(`${MEETING_API}/assignhost`, {
      hostId,
      ChimeMeetingId,
      ExternalMeetingId,
    });
    resolve(response?.data);
  });

/**

**/
export const createClassMeeting = (
  MeetingId: string,
  CreatorUserId: string,
  MeetingName: string,
  isRecurring: boolean = false,
  scheduledStartDateTime = null,
  repeatInMinutes = null
) =>
  new Promise(async (resolve) => {
    const response = await post(`${MEETING_API}/createclassmeeting`, {
      MeetingId,
      CreatorUserId,
      MeetingName,
      isRecurring,
      scheduledStartDateTime,
      repeatInMinutes,
    });
    resolve(response?.data);
  });

/**

**/
export const getTranscribePresignedUrl = (
  languageCode: string = 'en-US',
  sampleRate: number = 44100
) =>
  new Promise(async (resolve) => {
    const response = await post(`${MEETING_API}/SIGNED_API`, { languageCode, sampleRate });
    resolve(response?.data);
  });

/**

**/
export const isMeetingRunning = (ExternalMeetingId: string) =>
  new Promise(async (resolve) => {
    const response = await post(`${MEETING_API}/ismeetingrunning`, { ExternalMeetingId });
    resolve(response?.data?.isRunning);
  });

/**

**/
export const getAllMeetingMessages = (ExternalMeetingId: string, ChimeMeetingId: string) =>
  new Promise(async (resolve) => {
    const response = await post(`${MEETING_API}/messages`, { ExternalMeetingId, ChimeMeetingId });
    resolve(response?.data);
  });

/**
 * Gets Meeting Analysis After Host Of Meeting Ends The Meeting
 * @param {string} ExternalMeetingId - Meeting Id
 * @param {string} ChimeMeetingId - Chime Meeting Id
 * @return {object || null} - If meeting is created the  object will be returned
 **/
export const getMeetingAnalysis = (ExternalMeetingId: string, ChimeMeetingId: string) =>
  new Promise(async (resolve, reject) => {
    try {
      const resp = await post(`${MEETING_API}/meetinganalysis`, {
        ExternalMeetingId,
        ChimeMeetingId,
      });
      resolve(resp.data);
    } catch (e) {
      reject(Error(e));
    }
  });

/**
 * Non Host Leaves The Meeting
 * @param {string} ExternalMeetingId - Meeting Id
 * @param {string} ChimeMeetingId - Chime Meeting Id
 * @param {string} ExternalUserId - Users Id
 * @return {object || null} - If meeting is created the  object will be returned
 **/
export const leaveMeeting = (
  ExternalMeetingId: string,
  ChimeMeetingId: string,
  ExternalUserId: string
) =>
  new Promise(async (resolve, reject) => {
    try {
      const resp = await post(`${MEETING_API}/leave`, {
        ExternalMeetingId,
        ChimeMeetingId,
        ExternalUserId,
      });
      resolve(resp.data);
    } catch (e) {
      reject(Error(e));
    }
  });

/**
 * Get Presigned URL For Message Files Bucket
 * @param {string} ExternalMeetingId - Meeting Id
 * @param {string} ChimeMeetingId - Chime Meeting Id
 * @param {string} ExternalUserId - Users Id
 * @param {string} ContentType - file content
 * @param {string} FileName - file name
 * @return {object || null} -
 **/
export const getPresignedMessageFileBucketUrl = (
  ExternalMeetingId: string,
  ChimeMeetingId: string,
  ExternalUserId: string,
  ContentType: string,
  FileName: string
) =>
  new Promise(async (resolve, reject) => {
    try {
      const { url, fileId } = (
        await post(`${SIGNED_API}/messagefile`, {
          ExternalMeetingId,
          ChimeMeetingId,
          ExternalUserId,
          ContentType,
          FileName,
        })
      ).data;

      resolve({ url, fileId });
    } catch (e) {
      reject(Error(e));
    }
  });

/**
 * Download Message File
 * @param {string} ExternalMeetingId - Meeting Id
 * @param {string} ChimeMeetingId - Chime Meeting Id
 * @param {string} FileId - file id
 * @return {object || null} -
 **/
export const downloadMessageFile = (
  ExternalMeetingId: string,
  ChimeMeetingId: string,
  FileId: string
) =>
  new Promise(async (resolve, reject) => {
    try {
      const { url } = (
        await post(`${SIGNED_API}/downloadmessagefile`, {
          ExternalMeetingId,
          ChimeMeetingId,
          FileId,
        })
      ).data;
      resolve({ url });
    } catch (e) {
      reject(Error(e));
    }
  });

export const queryKendra = (search: string) =>
  new Promise(async (resolve) => {
    try {
      const response = await post(`${MEETING_API}/kendrasearch`, { search });
      resolve(response?.data);
    } catch (error) {
      resolve(null);
    }
  });

export const downloadKendraFile = (DocumentId: string) =>
  new Promise(async (resolve) => {
    try {
      const resp = await post(`${SIGNED_API}/kendras3url`, { DocumentId });
      resolve(resp?.data?.url);
    } catch (e) {
      resolve(null);
    }
  });

export const getKendraFilesUploadUrls = (files: string) =>
  new Promise(async (resolve) => {
    try {
      const resp = await post(`${SIGNED_API}/getkendrafileurls`, { files });
      resolve(resp?.data?.urls);
    } catch (e) {
      resolve(null);
    }
  });

export const getBroadcastPresignedS3Url = (Key: string, ContentType: string, RequestType: string) =>
  new Promise(async (resolve) => {
    try {
      const resp = await post(`${SIGNED_API}/getbroadcastuploadurl`, {
        Key,
        ContentType,
        RequestType,
      });
      resolve(resp?.data);
    } catch (e) {
      resolve(null);
    }
  });
export const downloadMp4 = (key: string, sessionId: string) =>
  new Promise(async (resolve) => {
    try {
      const resp = await post(`${SIGNED_API}/downloadmp4`, {
        key,
        sessionId,
      });
      resolve(resp?.data);
    } catch (e) {
      resolve(null);
    }
  });

export const upvoteQuestion = (
  SessionId: string,
  QuestionId: string,
  UserId: string,
  AddVote: boolean
) =>
  new Promise(async (resolve) => {
    try {
      const resp = await post(`${MEETING_API}/upvotequestion`, {
        SessionId,
        QuestionId,
        UserId,
        AddVote,
      });

      resolve(resp?.data?.urls);
    } catch (e) {
      resolve(null);
    }
  });

export const setQuestionHidden = (
  SessionId: string,
  UserId: string,
  QuestionId: string,
  Hidden: boolean
) =>
  new Promise(async (resolve) => {
    try {
      const resp = await post(`${MEETING_API}/hidequestion`, {
        SessionId,
        QuestionId,
        UserId,
        Hidden,
      });

      resolve(resp?.data?.urls);
    } catch (e) {
      resolve(null);
    }
  });

export const setQuestionAnswer = (
  SessionId: string,
  UserId: string,
  QuestionId: string,
  Answer: string
) =>
  new Promise(async (resolve) => {
    try {
      const resp = await post(`${MEETING_API}/answerquestion`, {
        SessionId,
        QuestionId,
        Answer,
        UserId,
      });

      resolve(resp?.data?.urls);
    } catch (e) {
      resolve(null);
    }
  });

export const getAllQuestions = (
  SessionId: string,
  QuestionIds: string[] = [],
  GetAll: boolean = false,
  UserId: string
) =>
  new Promise(async (resolve) => {
    try {
      const resp = await post(`${MEETING_API}/getallquestions`, {
        SessionId,
        QuestionIds,
        GetAll,
        UserId,
      });

      resolve(resp?.data);
    } catch (e) {
      resolve(null);
    }
  });

export const sqsQuestions = () =>
  new Promise(async (resolve) => {
    try {
      const resp = await post(`${MEETING_API}/sqsquestions`, {});

      // if (resp.error) {
      // }
      resolve(resp?.data);
    } catch (e) {
      resolve(null);
    }
  });
export const askQuestion = (
  Question: string,
  UserId: string,
  SessionId: string,
  UserName: string,
  TimeAsked: number
) =>
  new Promise(async (resolve) => {
    try {
      const resp = await post(`${MEETING_API}/createquestion`, {
        UserId,
        SessionId,
        Question,
        UserName,
        TimeAsked,
      });
      resolve(resp?.data);
    } catch (e) {
      resolve(null);
    }
  });

export const askForHelp = (Message: string, UserId: string, SessionId: string) =>
  new Promise(async (resolve) => {
    try {
      const resp = await post(`${MEETING_API}/helprequest`, {
        Message,
        UserId,
        SessionId,
      });
      resolve(resp?.data);
    } catch (e) {
      resolve(null);
    }
  });

export const authConferenceUser = (token: string) =>
  new Promise(async (resolve, reject) => {
    try {
      const resp = await post(`${MEETING_API}/authconferenceuser`, { token });

      resolve(resp?.data);
    } catch (e) {
      reject(new Error(e.message));
    }
  });

/**
 * Gets information about ECS tasks for streaming cluster
 * @return {object || null} - Returns object containing instance data
 **/
export const getECSTasksInfo = () =>
  new Promise(async (resolve) => {
    const response = await post(`${STREAM_API}/gettasks`);
    resolve(response?.data);
  });

/**
 * Stops indicated ECS Task
 * @param {string} taskId - Task Id or Full Task Arn
 * @return {object || null} - Returns success response
 **/
export const stopECSTask = (taskId: string) =>
  new Promise(async (resolve) => {
    const response = await post(`${STREAM_API}/stoprecording`, { taskId });
    resolve(response?.data);
  });

/**
 * Starts ECS Streaming Task
 * @param {string} meetingURL - URL of the chime meeting to be recorded
 * @param {string} rtmpURL - URL of the rtmp point to stream too
 * @return {object || null} - Returns object containing instance data
 **/
export const startStreamingTask = (meetingURL: string, rtmpURL: string) =>
  new Promise(async (resolve) => {
    const response = await post(`${STREAM_API}/startstream`, { meetingURL, rtmpURL });
    resolve(response?.data);
  });

/**
 * Gets information about Media Live Channels
 * @return {object || null} - Returns object containing Media Live channel data
 **/
export const getMediaLiveChannelsInfo = () =>
  new Promise(async (resolve) => {
    const response = await post(`${STREAM_API}/getchannels`);
    resolve(response?.data);
  });

/**
 * Stops Media Live Channel
 * @return {object || null} - Returns object containing Media Live channel data
 **/
export const stopMediaLiveChannel = (channelId: string) =>
  new Promise(async (resolve) => {
    const response = await post(`${STREAM_API}/stopchannel`, { channelId });
    resolve(response?.data);
  });

/**
 * Starts Media Live Channel
 * @return {object || null} - Returns object containing Media Live channel data
 **/
export const startMediaLiveChannel = (channelId: string) =>
  new Promise(async (resolve) => {
    const response = await post(`${STREAM_API}/startchannel`, { channelId });
    resolve(response?.data);
  });

/**
 * Deletes Media Live Channel
 * @return {object || null} - Returns object containing Media Live channel data
 **/
export const deleteMediaLiveChannel = (channelId: string) =>
  new Promise(async (resolve) => {
    const response = await post(`${STREAM_API}/deletechannel`, { channelId });
    resolve(response?.data);
  });

/**
 * Gets information about IVS Channels
 * @return {object || null} - Returns object containing IVS channel data
 **/
export const getIVSChannelsInfo = () =>
  new Promise(async (resolve) => {
    const response = await post(`${STREAM_API}/getivschannels`);
    resolve(response?.data);
  });

/**
 * Creates IVS Channel
 * @return {object || null} - Returns object containing IVS channel data
 **/
export const createIVSChannel = (name: string) =>
  new Promise(async (resolve) => {
    const response = await post(`${STREAM_API}/createivschannel`, { name });
    resolve(response?.data);
  });

/**
 * Deletes IVS channel
 * @return {object || null} - Returns object containing IVS channel data
 **/
export const deleteIVSChannel = (channelId: string) =>
  new Promise(async (resolve) => {
    const response = await post(`${STREAM_API}/deleteivschannel`, { channelId });
    resolve(response?.data);
  });

/**
 * Creates a MediaLive Channel
 * @return {object || null} - Returns channelId
 **/
export const createChannel = (
  chimeinputId: string,
  continueInputId: string,
  loopInputId: string,
  name: string,
  solutionId: string,
  ivsEndpoint: string,
  ivsKey: string
) =>
  new Promise(async (resolve) => {
    const config = {
      EncodingProfile: 'rtmp',
      Codec: 'AVC',
      Role: '',
      ChimeInputId: chimeinputId,
      LoopInputId: loopInputId,
      ContinueInputId: continueInputId,
      IVSEndpoint: ivsEndpoint,
      IVSStreamKey: ivsKey,
      SolutionId: solutionId,
      Name: name,
    };
    const response = await post(`${STREAM_API}/createchannel`, { config });
    resolve(response?.data);
  });

/**
 * Lists MediaLive Inputs
 * @return {object || null} - Returns Inputs Data
 **/
export const listInputs = () =>
  new Promise(async (resolve) => {
    const response = await post(`${STREAM_API}/listinputs`);
    resolve(response?.data);
  });

/**
 * Delets MediaLive Input
 * @return {object || null} - Returns Inputs Status
 **/
export const deleteInput = (inputId: string) =>
  new Promise(async (resolve) => {
    const response = await post(`${STREAM_API}/deleteinput`, { inputId });
    resolve(response?.data);
  });

/**
 * Delets MediaLive Input
 * @return {object || null} - Returns Inputs Status
 **/
export const createRTMPInput = (name: string, cidr: string) =>
  new Promise(async (resolve) => {
    const response = await post(`${STREAM_API}/addrtmpinput`, { cidr, name });
    resolve(response?.data);
  });

/**
 * Creates MediaLive Mp4 Input
 * @return {object || null} - Returns Inputs Status
 **/
type CreateMp4InputConfig = { StreamName: string; Signifier: string };
export const createMp4Input = (config: CreateMp4InputConfig) =>
  new Promise(async (resolve) => {
    const response = await post(`${STREAM_API}/addmp4input`, { config });
    resolve(response?.data);
  });

/**
 * Switches MediaLive Input
 * @return {object || null} - Returns Inputs Status
 **/
type SwitchInputConfig = {
  Type: string;
  ActionName: string;
  S3Path?: string;
  InputAttachmentName: string;
};
export const switchInput = (SessionId: string, ResourceId: string, config: SwitchInputConfig) =>
  new Promise(async (resolve) => {
    const response = await post(`${STREAM_API}/switchinput`, { SessionId, ResourceId, config });
    resolve(response?.data);
  });

/**
 * get conference object
 * @return {object || null} - returns conference object
 **/
export const getConferenceSession = (sessionId: string) =>
  new Promise(async (resolve) => {
    const response = await post(`${MEETING_API}/getconferencesession`, { sessionId });
    resolve(response?.data);
  });

/**
 * create conference object
 * @return {object || null} - returns conference object
 **/
export const createConferenceSession = (sessionId: string, sessionName: string) =>
  new Promise(async (resolve) => {
    const response = await post(`${MEETING_API}/createconferencesession`, {
      sessionId,
      sessionName,
    });

    resolve(response?.data);
  });

/**
 * adds s3 path to conference
 * @return {object || null} - returns conference object
 **/
type VideoConfig = {
  Name: string;
  Key: string;
  Bucket: string;
};
export const addVideoToConference = (SessionId: string, video: VideoConfig) =>
  new Promise(async (resolve) => {
    const response = await post(`${MEETING_API}/addvideotoconference`, { SessionId, video });
    resolve(response?.data);
  });

export const setConferenceStandby = (SessionId: string, video: VideoConfig) =>
  new Promise(async (resolve) => {
    const response = await post(`${MEETING_API}/setconferencestandby`, { SessionId, video });
    resolve(response?.data);
  });

/**
 * adds s3 path to conference
 * @return {object || null} - returns conference object
 **/
export const RemoveVideoFromConference = (SessionId: string, video: VideoConfig) =>
  new Promise(async (resolve) => {
    const response = await post(`${MEETING_API}/removevideofromconference`, { SessionId, video });
    resolve(response?.data);
  });

/**
 * Updates Conference
 * @return {object || null} - returns conference object
 **/
export const updateConference = async (session: string, sessionId: string, remove: boolean) =>
  new Promise(async (resolve) => {
    const response = await post(`${MEETING_API}/updateconference`, { session, sessionId, remove });
    resolve(response?.data);
  });

/**
 * BroadcastStatus
 * @param config - contains { ivsArn, channelId }
 * @return {object || null} - returns conference object
 **/
export const conferenceStatus = (SessionId: string, ResourceId: string) =>
  new Promise(async (resolve) => {
    const response = await post(`${STREAM_API}/broadcaststatus`, { SessionId, ResourceId });
    resolve(response?.data);
  });

/**
 * PresenterStatus
 * @param config - contains { ivsArn, channelId }
 * @return {object || null} - returns conference object
 **/
export const presenterStatus = (SessionId: string) =>
  new Promise(async (resolve) => {
    const response = await get(`${STREAM_API}/presenterstatus/${SessionId}`);
    resolve(response?.data);
  });

/**
 * BroadcastStatus
 * @param config - contains { ivsArn, channelId }
 * @return {object || null} - returns conference object
 **/
export const attendeeStatus = (SessionId: string) =>
  new Promise(async (resolve) => {
    const response = await get(`${STREAM_API}/attendeestatus/${SessionId}`);
    resolve(response?.data);
  });

/**
 * Get File data from S3
 * @param key - The object key in S3
 * @param bucket - The S3 bucket name
 * @return {object || null} - returns file Data
 **/
export const getFileData = (key: string, bucket: string) =>
  new Promise(async (resolve) => {
    const response = await post(`${STREAM_API}/getfiledata`, { key, bucket });
    resolve(response?.data);
  });

/**
 * Delete File from S3
 * @param key - The object key in S3
 * @return {object || null} - returns file Data
 **/
export const deleteFile = (key: string) =>
  new Promise(async (resolve) => {
    const response = await post(`${SIGNED_API}/deletebroadcastvideo`, { key });
    resolve(response?.data);
  });

/**
 * Delte a file from the conference bucket
 * @param key - The object key in S3
 * @param bucket - The S3 bucket name
 * @return {object || null} - returns conference object
 **/
export const deleteConferenceFile = (key: string, bucket: string) =>
  new Promise(async (resolve) => {
    const response = await post(`${STREAM_API}/deleteconferencefile`, { key, bucket });
    resolve(response?.data);
  });

/**
//  * Update The CW Event To Trigger The Step Function For Cronjob
//  * @param state - Either ENABLED or DISABLED
//  * @return {object } - returns success or error object
//  **/
// export const updateCloudwatchEvent = (state: string) =>
//   new Promise(async (resolve) => {
//     const response = await post(`${MEETING_API}/updatecloudwatchevent`, { state });
//     resolve(response?.data);
//   });

/**
 * getToken
 * @return {object || null} - returns token
 **/
export const getToken = (
  firstName: string,
  lastName: string,
  role: string,
  email: string,
  sessionId: string,
  sessionName: string
) =>
  new Promise(async (resolve) => {
    const response = await post(`${MEETING_API}/gettoken`, {
      firstName,
      lastName,
      role,
      email,
      sessionId,
      sessionName,
    });
    resolve(response?.data);
  });

/**
 * Delte a file from the conference bucket
 * @param SessionId - config
 * @return {object || null} - returns conference object
 **/

export const stopBroadcast = async (SessionId: string) =>
  new Promise(async (resolve) => {
    const response = await post(`${STREAM_API}/stopbroadcast`, { SessionId });
    resolve(response?.data);
  });

/**
 * Start a harvest job from mediapackage
 * @param {number} startTime -
 * @param {number} endTime -
 * @param {string} endpointId -
 * @param {string} SessionId -
 * @return {object || null} - returns conference object
 **/
export const startHarvest = async (
  startTime: string,
  endTime: string,
  endpointId: string,
  SessionId: string
) =>
  new Promise(async (resolve) => {
    const response = await post(`${STREAM_API}/startharvest`, {
      startTime,
      endTime,
      endpointId,
      SessionId,
    });
    resolve(response?.data);
  });

/**
 * Start a harvest job from mediapackage
 * @param {string} channelId -
 * @return {object || null} - returns conference object
 **/
export const listHarvest = async (channelId: string) =>
  new Promise(async (resolve) => {
    const response = await post(`${STREAM_API}/listharvest`, { channelId });
    resolve(response?.data);
  });

/**
 * Delte a file from the conference bucket
 * @param config - config
 * @return {object || null} - returns conference object
 **/
export const startBroadcast = async (SessionId: string) =>
  new Promise(async (resolve) => {
    const response = await post(`${STREAM_API}/startbroadcast`, { SessionId });
    resolve(response?.data);
  });

export const createTestQuestions = async (SessionId: string, Count: number) =>
  new Promise(async (resolve) => {
    const response = await post(`${MEETING_API}/createtestquestions`, { SessionId, Count });
    resolve(response?.data);
  });

export const deleteAllQuestionsTest = async (SessionId: string) =>
  new Promise(async (resolve) => {
    const response = await post(`${MEETING_API}/testdeletequestions`, { SessionId });
    resolve(response?.data);
  });

// export const testEventTrigger = () =>
//   new Promise(async (resolve) => {
//     let turnOn = true;
//     let IvsArn = 'arn:aws:ivs:us-east-1:472848567057:channel/KvmgoELYkuCE';
//     let event = {
//       detail: {
//         event_name: turnOn ? 'Stream Start' : 'Stream End',
//       },
//       resources: [IvsArn],
//     };
//     const response = await post(`${MEETING_API}/testeventtrigger`, event);
//     resolve(response?.data);
//   });

export const updateConferenceSessionUser = (
  UserId: string,
  SessionId: string,
  AttendenceType: string
) =>
  new Promise(async (resolve) => {
    const response = await post(`${MEETING_API}/updateconferencesessionuser`, {
      UserId,
      SessionId,
      AttendenceType,
    });
    resolve(response?.data);
  });

export const updateVodResource = (
  SessionId: string,
  VideoUrl: string,
  Name: any,
  Published: any,
  StartTime?: number,
  EndTime?: number
) =>
  new Promise(async (resolve) => {
    const response = await post(`${MEETING_API}/updatevodresource`, {
      SessionId,
      VideoUrl,
      Name,
      Published,
      StartTime,
      EndTime,
    });
    resolve(response?.data);
  });

export const getCachedQuestions = (SessionId: string) =>
  new Promise(async (resolve) => {
    const response = await get(`${QUESTIONS_CF_API}?sessionid=${SessionId}`);
    resolve(response?.data);
  });

export const blockUser = (SessionId: string, BlockUserId: string, shouldBlock: boolean) =>
  new Promise(async (resolve) => {
    const response = await post(`${MEETING_API}/blockuser`, {
      SessionId,
      BlockUserId,
      shouldBlock,
    });
    resolve(response?.data);
  });

export const getLiveSessions = () =>
  new Promise(async (resolve) => {
    const response = await post(`${STREAM_API}/getlivesessions`);
    resolve(response?.data);
  });

export const getSessions = () =>
  new Promise(async (resolve) => {
    const response = await post(`${STREAM_API}/getsessions`);
    resolve(response?.data);
  });

export const getResourceStatus = (SessionId: string, ResourceId: string) =>
  new Promise(async (resolve) => {
    const response = await post(`${STREAM_API}/getresourcestatus`, { SessionId, ResourceId });
    resolve(response?.data);
  });

export const getEc2Instances = () =>
  new Promise(async (resolve) => {
    const response = await post(`${STREAM_API}/getec2instances`);
    resolve(response?.data);
  });

export const restartffmpeg = (ec2Id: string) =>
  new Promise(async (resolve) => {
    const response = await post(`${STREAM_API}/restartffmpeg`, { ec2Id });
    resolve(response?.data);
  });

export const restartFirefox = (ec2Id: string) =>
  new Promise(async (resolve) => {
    const response = await post(`${STREAM_API}/restartfirefox`, { ec2Id });
    resolve(response?.data);
  });

export const restartEc2 = (SessionId: string, ResourceId: string) =>
  new Promise(async (resolve) => {
    const response = await post(`${STREAM_API}/restartec2`, { SessionId, ResourceId });
    resolve(response?.data);
  });

export const administratorLogin = (Username: string, Password: string) =>
  new Promise(async (resolve) => {
    const response = await post(`${MEETING_API}/adminlogin`, { Username, Password });
    resolve(response?.data);
  });

export const startMp4Conversion = (inputPath: string, sessionId: string) =>
  new Promise(async (resolve) => {
    console.log('In MP4 conversion');
    const response = await post(`${STREAM_API}/convertarchive`, { inputPath, sessionId });
    console.log('after');
    resolve(response?.data);
  });

export const listArchives = (sessionId: string) =>
  new Promise(async (resolve) => {
    const response = await post(`${STREAM_API}/listarchive`, { sessionId });
    resolve(response?.data);
  });

export const listConvertJobs = (status: string, sessionId: string) =>
  new Promise(async (resolve) => {
    const response = await post(`${STREAM_API}/listconvertjobs`, { status, sessionId });
    resolve(response?.data);
  });
